import { useEffect, useState, useRef } from 'react';
import config from '../config';

function useSSE(endpoint, tokenOrHasToken, onMessage, onError) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [internalToken, setInternalToken] = useState(null);
  const retryTimeout = useRef(null);
  const retryCount = useRef(0);
  
  const SILENT_RETRIES = 3; 

  // Determine effective token: either passed directly as string or from internal state
  const effectiveToken = typeof tokenOrHasToken === 'string' ? tokenOrHasToken : internalToken;
  const shouldConnect = typeof tokenOrHasToken === 'string' ? !!tokenOrHasToken : (tokenOrHasToken === true ? !!internalToken : true);

  const getUrl = () => {
    const protocol = config.isDev === true ? "http://" : "https://";
    const port = config.isDev === true ? `:${config.port}` : "";
    const tokenParam = effectiveToken ? `?token=${effectiveToken}` : "";
    return `${protocol}${config.backend}${port}/${endpoint}${tokenParam}`;
  };

  useEffect(() => {
    if (!shouldConnect) return;

    let eventSource = new EventSource(getUrl());

    const connect = () => {
      eventSource.onmessage = (event) => {
        try {
          const newData = JSON.parse(event.data);
          setData(newData);
          setError(null);
          retryCount.current = 0;
          
          if (onMessage) {
            onMessage(newData);
          }
        } catch (e) {
          console.error("SSE JSON parse error:", e, event.data);
        }
      };

      eventSource.onerror = (err) => {
        eventSource.close();
        retryCount.current += 1;

        const errorMessage = 'Connection lost. Trying to reconnect...';
        if (retryCount.current > SILENT_RETRIES) {
          setError(errorMessage);
          if (onError) onError(err, true);
        } else {
          setError(null);
          if (onError) onError(err, false);
          console.warn(`SSE connection lost. Retrying silently (${retryCount.current}/${SILENT_RETRIES})...`);
        }

        const delay = Math.min(30000, 1000 * 2 * retryCount.current);
        retryTimeout.current = setTimeout(() => {
          eventSource = new EventSource(getUrl());
          connect();
        }, delay);
      };
    };

    connect();

    return () => {
      eventSource.close();
      if (retryTimeout.current) clearTimeout(retryTimeout.current);
    };

  }, [endpoint, effectiveToken, shouldConnect]); // Reconnect if endpoint or token changes

  return { data, error, setToken: setInternalToken };
}

export default useSSE;